/**
 * Adaptador de exportação da tela KPIs PPCP (DBR): converte o payload de
 * GET /api/producao/dbr para o shape `{titulo, resumo, secoes}` que
 * `salvarPDF` / `salvarExcel` / `salvarCSV` (utils/exportacao) consomem —
 * mesmo padrão de pages/Financeiro/_shared/exportarRelatorios.js.
 *
 * `incluirValores: false` (gate de preço — usuário sem preco:view) remove
 * as linhas/colunas em R$ do arquivo: o export tem que respeitar o mesmo
 * gate da tela, senão vira vazamento por download.
 */
import { salvarCSV, salvarExcel, salvarPDF } from "../../../utils/exportacao";
import { LABEL_CATEGORIA } from "../categorias6m";

const SAVERS = { pdf: salvarPDF, excel: salvarExcel, csv: salvarCSV };

export function exportarDbr(formato, payload, { incluirValores = true } = {}) {
  const saver = SAVERS[formato];
  if (!saver) throw new Error(`Formato desconhecido: ${formato}`);
  if (!payload) return null;

  const t = payload.tambor || {};
  const th = payload.throughput || {};
  const pulmao = payload.pulmao || {};

  const resumo = [
    { label: "Janela (dias)", value: payload.janela?.dias ?? "", type: "number" },
    { label: "Dias úteis na janela", value: payload.janela?.dias_uteis ?? "", type: "number" },
    ...(incluirValores
      ? [{ label: "Throughput (R$)", value: Number(th.valor_rs || 0), type: "currency" }]
      : []),
    { label: "Ambientes carregados", value: th.ambientes ?? 0, type: "number" },
    { label: "WIP total (ambientes)", value: payload.wip?.total ?? 0, type: "number" },
    { label: "Etapa-gargalo (Tambor)", value: t.etapa?.label || "Sem gargalo ativo", type: "text" },
    { label: "Utilização do Tambor %", value: t.utilizacao_pct ?? "", type: "percentage" },
    { label: "Ritmo do Tambor (amb/dia)", value: t.ritmo_ambientes_dia ?? "", type: "number" },
    { label: "Pulmão — críticos (vermelho)", value: pulmao.vermelho?.qtd ?? 0, type: "number" },
    { label: "Pulmão — sem baseline de buffer", value: pulmao.sem_buffer ?? 0, type: "number" },
  ];

  const secoes = [
    {
      id: "wip_etapas",
      label: "WIP por etapa",
      columns: [
        { key: "etapa", label: "Etapa", type: "text" },
        { key: "fase", label: "Fase", type: "text" },
        { key: "ambientes", label: "Ambientes", type: "number" },
        { key: "pecas_em_fila", label: "Peças em fila", type: "number" },
        { key: "tambor", label: "Tambor", type: "text" },
      ],
      rows: (payload.wip?.por_etapa || []).map((e) => ({
        etapa: e.label,
        fase: e.fase || "",
        ambientes: e.ambientes,
        pecas_em_fila: e.pecas_em_fila,
        tambor: e.eh_tambor ? "sim" : "",
      })),
    },
    {
      id: "throughput_serie",
      label: "Throughput por período",
      columns: [
        { key: "inicio", label: "Início do bucket", type: "date" },
        { key: "ambientes", label: "Ambientes carregados", type: "number" },
        ...(incluirValores
          ? [{ key: "valor_rs", label: "Valor (R$)", type: "currency" }]
          : []),
      ],
      rows: (th.serie || []).map((b) => ({
        inicio: b.inicio,
        ambientes: b.ambientes,
        ...(incluirValores ? { valor_rs: Number(b.valor_rs || 0) } : {}),
      })),
    },
    {
      id: "despacho",
      label: `Despacho do Tambor${t.etapa?.label ? ` — ${t.etapa.label}` : ""}`,
      columns: [
        { key: "ordem", label: "#", type: "number" },
        { key: "ambiente", label: "Ambiente", type: "text" },
        { key: "pedido", label: "Pedido", type: "text" },
        { key: "cliente", label: "Cliente", type: "text" },
        { key: "buffer_pct", label: "Consumo buffer %", type: "percentage" },
        { key: "zona", label: "Zona", type: "status" },
        { key: "pecas", label: "Peças no posto", type: "number" },
        { key: "proxima", label: "Próx. etapa", type: "text" },
      ],
      rows: (payload.despacho?.fila || []).map((linha) => ({
        ordem: linha.ordem,
        ambiente: `${linha.ambiente?.numero || ""} · ${linha.ambiente?.nome || ""}`,
        pedido: linha.ambiente?.pedido_numero || "",
        cliente: linha.ambiente?.cliente_nome || "",
        buffer_pct: linha.consumo_buffer?.pct ?? "",
        zona: linha.consumo_buffer?.zona || "",
        pecas: linha.pecas_no_posto ?? 0,
        proxima: linha.proxima_etapa?.label || "",
      })),
    },
    {
      id: "criticos",
      label: "Ambientes críticos (zona vermelha)",
      columns: [
        { key: "pedido", label: "Pedido", type: "text" },
        { key: "ambiente", label: "Ambiente", type: "text" },
        { key: "cliente", label: "Cliente", type: "text" },
        { key: "etapa", label: "Etapa atual", type: "text" },
        { key: "buffer_pct", label: "Consumo buffer %", type: "percentage" },
        { key: "atraso_min", label: "Atraso (min)", type: "number" },
        { key: "causa", label: "Causa dominante", type: "text" },
      ],
      rows: (payload.criticos || []).map((c) => ({
        pedido: c.pedido_numero || "",
        ambiente: `${c.numero || ""} · ${c.nome || ""}`,
        cliente: c.cliente_nome || "",
        etapa: c.etapa_atual?.label || "",
        buffer_pct: c.consumo_buffer?.pct ?? "",
        atraso_min: c.atraso_min ?? 0,
        causa: LABEL_CATEGORIA[c.causa_dominante] || "",
      })),
    },
  ];

  return saver({
    titulo: `Indicadores PPCP (DBR) — janela ${payload.janela?.dias ?? ""}d`,
    resumo,
    secoes,
  });
}
